'use client'

import { Box, Button, Container, Stack, Typography } from '@mui/material'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import { AICLAIMS_WELCOME_PATH } from '../../config/aiclaims-routes'
import LoginModal from '../../components/dashboard/LoginModal'
import { FlexCol } from '../../components/layout'
import useAuth from '../../hooks/useAuth'
import { navigateToAllowedBrandHome } from '../../lib/crossDomainAuth'
import {
  canUseAiClaimsProduct,
  effectiveBrandAccess,
} from '../../lib/pageAccess'
import { sanitizeInternalRedirect } from '../../utils/safeRedirect'

function PortalEntryHero({
  isSignedIn,
  hasPortalAccess,
  onSignIn,
}: {
  isSignedIn: boolean
  hasPortalAccess: boolean
  onSignIn: () => void
}) {
  return (
    <Box
      // biome-ignore lint/plugin: decorative full-viewport hero gradient
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, #f8fafc 0%, #e2f4f7 45%, #ffffff 100%)',
        color: 'text.primary',
      }}
    >
      <Container
        maxWidth="md"
        // biome-ignore lint/plugin: responsive hero band padding
        sx={{ py: { xs: 8, md: 14 } }}
      >
        <FlexCol
          gap="xl"
          align="center"
          // biome-ignore lint/plugin: centred hero copy
          sx={{ textAlign: 'center' }}
        >
          <Box
            component="img"
            src="/AiClaimsBlackLogo.svg"
            alt="AI Claims logo"
            // biome-ignore lint/plugin: logo media dimensions
            sx={{ width: 220, maxWidth: '70vw' }}
          />
          <FlexCol
            gap="md"
            // biome-ignore lint/plugin: hero copy column max-width
            sx={{ maxWidth: 720 }}
          >
            <Typography
              variant="overline"
              // biome-ignore lint/plugin: AI Claims brand accent has no theme token
              color="#0891b2"
            >
              Portal Access
            </Typography>
            <Typography variant="display">
              Access the AI Claims operations portal.
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Sign in to review dashboards, recent runs, workflows, cases, and
              operational cost views for the AI Claims environment.
            </Typography>
          </FlexCol>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button variant="contained" size="large" onClick={onSignIn}>
              {!isSignedIn
                ? 'Sign in to Portal'
                : hasPortalAccess
                  ? 'Open Portal'
                  : 'No Portal Access'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              component="a"
              href="/privacy"
            >
              Privacy
            </Button>
          </Stack>
        </FlexCol>
      </Container>
    </Box>
  )
}

export default function PortalEntryPage() {
  const router = useRouter()
  const { user, isLoading } = useAuth()
  const [openLoginModal, setOpenLoginModal] = useState(false)

  useEffect(() => {
    const currentSearchParams = new URLSearchParams(window.location.search)
    const shouldOpenLogin = currentSearchParams.get('login') === 'true'

    if (isLoading) {
      return
    }

    if (user) {
      const brandAccess = effectiveBrandAccess(user)
      if (canUseAiClaimsProduct(brandAccess)) {
        const nextUrl = sanitizeInternalRedirect(
          currentSearchParams.get('next'),
          window.location.origin,
        )
        router.replace(nextUrl ?? AICLAIMS_WELCOME_PATH)
      } else if (brandAccess === 'wysehub') {
        navigateToAllowedBrandHome(brandAccess)
      } else {
        setOpenLoginModal(false)
        if (
          window.location.pathname !== '/portal-entry' ||
          window.location.search
        ) {
          router.replace('/portal-entry')
        }
      }
      return
    }

    if (shouldOpenLogin) {
      setOpenLoginModal(true)
      const nextUrl = sanitizeInternalRedirect(
        currentSearchParams.get('next'),
        window.location.origin,
      )
      if (nextUrl) {
        localStorage.setItem('loginRedirectUrl', nextUrl)
      } else {
        localStorage.removeItem('loginRedirectUrl')
      }
    }
  }, [isLoading, router, user])

  return (
    <>
      <PortalEntryHero
        isSignedIn={!!user}
        hasPortalAccess={canUseAiClaimsProduct(effectiveBrandAccess(user))}
        onSignIn={() => {
          if (!user) {
            setOpenLoginModal(true)
            return
          }
          const brandAccess = effectiveBrandAccess(user)
          if (canUseAiClaimsProduct(brandAccess)) {
            router.push(AICLAIMS_WELCOME_PATH)
          } else if (brandAccess === 'wysehub') {
            navigateToAllowedBrandHome(brandAccess)
          } else {
            router.push('/unauthorised')
          }
        }}
      />
      <LoginModal
        open={openLoginModal}
        onClose={() => setOpenLoginModal(false)}
      />
    </>
  )
}
